import { useEffect, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import {
  Form,
  IconButton,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TextInput
} from '@carbon/react';
import { Information, TrashCan } from '@carbon/react/icons';
import { entries, fromPairs, keyBy } from 'lodash';

import { SUB_GROUP_OVERRIDE } from 'models/subGroupOverride';
import { formatNumeric } from 'utils/format';
import styles from 'theme/features/outputs/workstation-edit-modal.module.scss';

const WorkstationEditModal = ({
  open,
  employeePopulation,
  isCic = false,
  workstations,
  workstationEdits,
  onCancel,
  onSubmit
}) => {
  const [edits, setEdits] = useState(workstationEdits);

  useEffect(() => {
    if (open) {
      setEdits(
        fromPairs(workstationEdits.map(({ employeePopulationId, quantity }) => [employeePopulationId, quantity]))
      );
    }
  }, [open]);

  const workstationByPopulationId = keyBy(workstations, 'employeePopulationId');

  const handleSubmit = () =>
    onSubmit(
      entries(edits)
        .map(([id, value]) => ({
          employeePopulationId: id,
          quantity: value
        }))
        .filter(({ quantity }) => quantity !== null)
    );

  return (
    <Modal
      open={open}
      size="md"
      modalHeading="Workstation Customization"
      primaryButtonText="Save"
      secondaryButtonText="Cancel"
      onRequestClose={onCancel}
      onRequestSubmit={handleSubmit}
      onSecondarySubmit={onCancel}
    >
      <p>
        Use the following table to customize the number of workstations assigned to a particular business unit. By
        default, each business unit has a predetermined number of workstations allocated. Use this page to customize
        these allocations based on your team's needs and preferences.
      </p>
      <div className={styles.wrapper}>
        <p>Group Population and Workstation Summary</p>
        <Form onSubmit={handleSubmit}>
          <Table size="sm" className={styles.table}>
            <TableHead>
              <TableRow>
                <TableHeader>Business Unit</TableHeader>
                <TableHeader>Sub-group</TableHeader>
                <TableHeader>Headcount</TableHeader>
                <TableHeader>Attending Population</TableHeader>
                <TableHeader>Default Workstations</TableHeader>
                <TableHeader>
                  Customized Workstations{' '}
                  <button
                    className={styles.tooltip}
                    type="button"
                    data-tooltip-id="customized-workstations-tooltip"
                    data-tooltip-place="bottom"
                    data-tooltip-position-strategy="fixed"
                  >
                    <Information />
                  </button>
                  <Tooltip id="customized-workstations-tooltip" style={{ maxWidth: 240, zIndex: 2 }}>
                    Enter the total workstations required. This will override the default value.
                  </Tooltip>
                </TableHeader>
                <TableHeader />
              </TableRow>
            </TableHead>
            <TableBody>
              {employeePopulation
                ?.filter((b) => b.headcount)
                ?.filter((b) =>
                  isCic
                    ? b.subGroup.override === SUB_GROUP_OVERRIDE.cic
                    : b.subGroup.override !== SUB_GROUP_OVERRIDE.cic
                )
                .map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>{row.businessUnit.name}</TableCell>
                    <TableCell>{row.subGroup.name}</TableCell>
                    <TableCell>{formatNumeric(row.headcount)}</TableCell>
                    <TableCell>
                      {formatNumeric(Math.round((row.attendanceRate ?? row.subGroup.attendanceRate) * row.headcount))}
                    </TableCell>
                    <TableCell>{formatNumeric(workstationByPopulationId[row.id]?.quantity ?? 0)}</TableCell>
                    <TableCell>
                      <TextInput
                        id={`${isCic ? 'cic' : 'non-cic'}-workstation-${row.id}`}
                        placeholder="Enter a number"
                        type="number"
                        min={0}
                        labelText=""
                        hideLabel
                        value={edits[row.id] ?? ''}
                        onChange={(e) =>
                          setEdits({ ...edits, [row.id]: e.target.value === '' ? null : Number(e.target.value) })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {edits[row.id] != null && (
                        <IconButton
                          label="Delete"
                          kind="ghost"
                          size="sm"
                          onClick={() => setEdits({ ...edits, [row.id]: null })}
                        >
                          <TrashCan size={20} />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Form>
      </div>
    </Modal>
  );
};

export default WorkstationEditModal;
